import { PreviewPageNavigator } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/PreviewPageNavigator'
import {
  PageListWrapper,
  PageSlot,
  PageSpacer,
  ScrollContainer,
} from 'features/knowledgeDocumentViewer/components/viewer/continuous/continuousScrollStyles'
import { useContinuousPinchZoom } from 'features/knowledgeDocumentViewer/components/viewer/continuous/hooks/useContinuousPinchZoom'
import { useContinuousScrollSync } from 'features/knowledgeDocumentViewer/components/viewer/continuous/hooks/useContinuousScrollSync'
import {
  MAX_ZOOM,
  useContinuousZoom,
} from 'features/knowledgeDocumentViewer/components/viewer/continuous/hooks/useContinuousZoom'
import { useDragToScroll } from 'features/knowledgeDocumentViewer/components/viewer/continuous/hooks/useDragToScroll'
import { useVirtualPageWindow } from 'features/knowledgeDocumentViewer/components/viewer/continuous/hooks/useVirtualPageWindow'
import { buildPageLayoutMetrics } from 'features/knowledgeDocumentViewer/components/viewer/continuous/pageLayoutMetrics'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/ZoomControls'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CmmsScrollPage } from './CmmsScrollPage'
import type { WorkOrderTextRun } from './workOrderHighlights'
import { SHEET_HEIGHT, SHEET_WIDTH } from './workOrderSheet'

const PAGE_GAP = 8
const PADDING = 16
const PAGE_SIZE = { w: SHEET_WIDTH, h: SHEET_HEIGHT }

export interface CmmsScrollViewerProps {
  pdfUrl: string
  totalPages: number
  currentPage: number
  onPageChange: (page: number) => void
  alt: string
  highlights: WorkOrderTextRun[]
}

export function CmmsScrollViewer({
  pdfUrl,
  totalPages,
  currentPage,
  onPageChange,
  alt,
  highlights,
  onToggleFullscreen,
  isFullscreen = false,
}: CmmsScrollViewerProps & {
  onToggleFullscreen: () => void
  isFullscreen?: boolean
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const lastFramed = useRef<WorkOrderTextRun[] | null>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const observer = new ResizeObserver(([entry]) => {
      if (entry)
        setSize({ w: entry.contentRect.width, h: entry.contentRect.height })
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])
  const pageHeightAtZoom = useCallback(
    (zoom: number) => SHEET_HEIGHT * zoom,
    []
  )
  const zoom = useContinuousZoom(
    scrollRef,
    PAGE_SIZE,
    size,
    PADDING,
    PAGE_GAP,
    totalPages,
    pageHeightAtZoom
  )
  useDragToScroll(scrollRef)
  useContinuousPinchZoom(
    scrollRef,
    listRef,
    zoom.snapshotZoomAnchor,
    zoom.applyAnchoredZoom,
    zoom.minZoom,
    MAX_ZOOM
  )
  const pageHeight = useCallback(
    () => SHEET_HEIGHT * zoom.effectiveZoom,
    [zoom.effectiveZoom]
  )
  const {
    currentPage: visiblePage,
    loadedRange,
    handleExternalPageChange,
  } = useContinuousScrollSync(
    scrollRef,
    totalPages,
    pageHeight,
    PAGE_GAP,
    currentPage,
    onPageChange
  )
  const metrics = useMemo(
    () => buildPageLayoutMetrics(totalPages, pageHeight, PAGE_GAP),
    [totalPages, pageHeight]
  )
  const pageWindow = useVirtualPageWindow(
    loadedRange,
    totalPages,
    PAGE_GAP,
    metrics
  )
  useEffect(
    () => handleExternalPageChange(currentPage),
    [currentPage, handleExternalPageChange]
  )
  useEffect(() => {
    const first = highlights[0]
    const container = scrollRef.current
    if (
      !first ||
      !container ||
      !zoom.effectiveZoom ||
      lastFramed.current === highlights
    )
      return
    lastFramed.current = highlights
    container.scrollTo({
      top: Math.max(
        0,
        PADDING +
          (metrics.tops[first.page - 1] ?? 0) +
          first.box.top * pageHeight() -
          container.clientHeight / 3
      ),
      behavior: 'instant',
    })
  }, [highlights, metrics, pageHeight, zoom.effectiveZoom])
  const width = SHEET_WIDTH * zoom.effectiveZoom
  return (
    <>
      <ScrollContainer
        ref={scrollRef}
        $opacity={1}
        tabIndex={0}
        aria-label={alt}>
        <PageListWrapper ref={listRef} $gap={PAGE_GAP} $padding={PADDING}>
          {pageWindow.topSpacerHeight > 0 ? (
            <PageSpacer $width={width} $height={pageWindow.topSpacerHeight} />
          ) : null}
          {pageWindow.pages.map((page) => (
            <PageSlot
              key={page}
              data-page={page}
              $width={width}
              $height={pageHeight()}>
              <CmmsScrollPage
                pdfUrl={pdfUrl}
                page={page}
                totalPages={totalPages}
                alt={alt}
                highlights={highlights}
              />
            </PageSlot>
          ))}
          {pageWindow.bottomSpacerHeight > 0 ? (
            <PageSpacer
              $width={width}
              $height={pageWindow.bottomSpacerHeight}
            />
          ) : null}
        </PageListWrapper>
      </ScrollContainer>
      <PreviewPageNavigator
        currentPage={visiblePage}
        totalPages={totalPages}
        onPageChange={handleExternalPageChange}
      />
      <ZoomControls
        onZoomIn={zoom.zoomIn}
        onZoomOut={zoom.zoomOut}
        onResetZoom={zoom.resetZoom}
        zoomOutDisabled={zoom.isAtMinZoom}
        zoomInDisabled={zoom.isAtMaxZoom}
        onToggleFullscreen={onToggleFullscreen}
        isFullscreen={isFullscreen}
      />
    </>
  )
}
